import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { noOp } from '@/utils/helpers'
import logger from '@/utils/logger'

import { useUser } from '@/client/context/user'

import Form from '@/components/UI/Form/Form'
import type {
  FormBlocksComponentProps,
  FormInputType,
  FormQuestion,
  FormValue,
} from '@/components/UI/Form/types'

import { DEFAULT_PAGE_NUMBER_QUERY_KEY } from './constants'
import { parseQueryValue, sanitizeQueryValue } from './helpers'

function DateFilterBlocks({ rows, renderQuestion }: FormBlocksComponentProps): JSX.Element {
  const blocks = rows
    .flatMap(row => row.blocks)
    .filter((block): block is FormQuestion => block.display !== 'display')
  return (
    <Stack direction="row" spacing="8px" width="100%">
      {blocks.map(block => (
        <Flex key={block.name} direction="column" gap="2px" flex={1}>
          {block.label != null ? <Text fontWeight="medium">{String(block.label)}</Text> : null}
          {renderQuestion(block)}
        </Flex>
      ))}
    </Stack>
  )
}

type DateFilterWidgetParameters = {
  startDateKey: string
  endDateKey: string
  startDateLabel: string
  endDateLabel: string
  hideHelpText?: boolean
}
function DateFilterWidget({
  startDateKey,
  endDateKey,
  startDateLabel,
  endDateLabel,
  hideHelpText = false,
}: DateFilterWidgetParameters): JSX.Element {
  const router = useRouter()
  const handleChange = useCallback(
    (name: string, value: FormValue) => {
      if (value == null) {
        const { [name]: _, ...rest } = router.query
        router
          .replace(
            {
              query: {
                ...rest,
              },
            },
            undefined,
            { scroll: false }
          )
          .catch(err => {
            logger.error('Navigation failed', { err })
          })
        return
      }

      const date = DateTime.fromISO(`${value}`)
      if (date.isValid) {
        const pageReset =
          router.query[DEFAULT_PAGE_NUMBER_QUERY_KEY] != null
            ? { [DEFAULT_PAGE_NUMBER_QUERY_KEY]: 1 }
            : {}
        router
          .replace(
            {
              query: {
                ...router.query,
                [name]: sanitizeQueryValue(date.toFormat('yyyy-LL-dd')),
                ...pageReset,
              },
            },
            undefined,
            { scroll: false }
          )
          .catch(err => {
            logger.error('Navigation failed', { err })
          })
      }
    },
    [router]
  )

  const startDate = parseQueryValue(startDateKey, router.query)
  const endDate = parseQueryValue(endDateKey, router.query)

  const initialStart = startDate != null ? DateTime.fromISO(startDate).toISO() : null
  const initialEnd = endDate != null ? DateTime.fromISO(endDate).toISO() : null

  const inputs: FormInputType[] = useMemo(
    () => [
      {
        name: startDateKey,
        type: 'date',
        label: startDateLabel,
        initialValue: initialStart,
        maxDate: initialEnd,
        size: 2,
      },
      {
        name: endDateKey,
        type: 'date',
        label: endDateLabel,
        initialValue: initialEnd,
        minDate: initialStart,
        size: 2,
      },
    ],
    [endDateKey, endDateLabel, initialEnd, initialStart, startDateKey, startDateLabel]
  )

  return (
    <Box flex={1}>
      <Form
        name="Date Picker"
        blocks={inputs}
        onChange={handleChange}
        onSubmit={noOp}
        FooterComponent={() => null}
        BlocksComponent={DateFilterBlocks}
      />
      {!hideHelpText ? (
        <Text fontSize="small" mt="8px" color="fg.subtle">
          To remove a date filter, just select the entire textbox and press &quot;delete&quot;.
        </Text>
      ) : null}
    </Box>
  )
}

export default function useDateFilter({
  startDateKey = 'start',
  endDateKey = 'end',
  startDateLabel = 'Start Date',
  endDateLabel = 'End Date',
  hideHelpText = false,
}: Partial<DateFilterWidgetParameters>): {
  startDate: DateTime | null
  endDate: DateTime | null
  DateFilterWidget: React.FC
} {
  const user = useUser()
  const router = useRouter()

  const startQuery = parseQueryValue(startDateKey, router.query)
  const start =
    startQuery != null ? DateTime.fromISO(startQuery, { zone: user.timezone }).startOf('day') : null

  const endQuery = parseQueryValue(endDateKey, router.query)
  const end =
    endQuery != null
      ? DateTime.fromISO(endQuery, { zone: user.timezone })
          .startOf('day')
          .plus({
            days: 1,
          })
          // Include the entire end date
          .minus({
            milliseconds: 1,
          })
      : null

  return {
    startDate: start != null && start.isValid ? start : null,
    endDate: end != null && end.isValid ? end : null,
    DateFilterWidget: () => (
      <DateFilterWidget
        startDateKey={startDateKey}
        endDateKey={endDateKey}
        startDateLabel={startDateLabel}
        endDateLabel={endDateLabel}
        hideHelpText={hideHelpText}
      />
    ),
  }
}
